import { FC, Fragment, lazy, ReactNode, Suspense } from "react";

import { TraitConfig, TraitType } from "@hightouch/lib/query/visual/types";
import { Code, Column, IconBox, Row, Text } from "@hightouch/htui";

import { FilterableColumn, TraitDefinition } from "src/types/visual";
import { getRelatedModelSection } from "src/components/explore/filter-popover/constants";
import { getPropertyNameFromColumn } from "src/components/explore/visual/utils";

import { PredictorType } from "@hightouch/lib/predictive-traits/types";

const FormulaTraitEditor = lazy(() =>
  import("src/pages/traits/components/formula-trait-editor").then((m) => ({
    default: m.FormulaTraitEditor,
  })),
);
import {
  parseTraitConfig,
  TRAIT_TYPE_LABELS,
} from "src/components/traits/utils";
import { ReadOnlyConditions } from "src/components/explore/read-only-conditions";
import {
  PredictiveTraitCalculationReadonly,
  PredictiveTraitCalculationReadonlyProps,
} from "src/pages/traits/components/predictive-trait-calculation";
import {
  RelationshipWithoutFilterableColumnsFragment,
  useCatalogCollectionQuery,
} from "src/graphql";
import { CatalogRecommendationTraitCalculationReadOnly } from "src/pages/traits/components/catalog-recommendation-trait-calculation";
import { LookalikeAudienceTraitCalculationReadOnly } from "src/pages/traits/components/lookalike-audience-trait-calculation";
import type { TrackedAudienceMembershipConfig } from "src/pages/traits/components/tracked-audience-list-form";

type Props = {
  type: TraitType;
  config: TraitConfig;
  parentModel:
    | {
        name: string;
        filterable_audience_columns: FilterableColumn[];
        traits: TraitDefinition[];
        id: string;
        connection: { id: string } | null;
        relationships: RelationshipWithoutFilterableColumnsFragment[];
      }
    | undefined;
  relatedModel:
    | {
        event:
          | {
              timestamp_column: string;
              event_model_event_sources?: readonly unknown[] | null;
            }
          | null
          | undefined;
        is_schema: boolean | null;
        primary_key: string | null;
        name: string;
      }
    | undefined;
  predictor?: PredictiveTraitCalculationReadonlyProps["predictor"];
  hideConditions?: boolean;
  hideModel?: boolean;
};

// Used for props to maintain referential integrity between renders
const EMPTY_ARRAY = [];

const PredictiveCalculationSummary: FC<{
  predictor: PredictiveTraitCalculationReadonlyProps["predictor"];
  parentModel: Props["parentModel"];
}> = ({ predictor, parentModel }) => {
  const catalogCollectionId =
    predictor &&
    "catalog_collection_id" in predictor &&
    predictor.catalog_collection_id;
  const { data: storedCatalogCollection } = useCatalogCollectionQuery(
    { id: catalogCollectionId || "" },
    {
      select: (data) => data.catalog_collections_by_pk,
      enabled: Boolean(catalogCollectionId),
    },
  );
  const catalogCollection =
    predictor && "catalog_collection_id" in predictor
      ? storedCatalogCollection
      : predictor?.catalogId
        ? {
            catalog_id: predictor.catalogId,
            filter: predictor?.catalogDefinition,
          }
        : undefined;

  const isLookalike =
    (predictor &&
      "type" in predictor &&
      predictor.type === PredictorType.LOOKALIKE) ||
    (predictor && "baseAudienceId" in predictor && predictor.baseAudienceId);

  if (isLookalike) {
    const baseAudienceId = (
      predictor && "baseAudienceId" in predictor
        ? predictor.baseAudienceId
        : predictor && "base_audience_id" in predictor
          ? predictor.base_audience_id
          : undefined
    ) as string | number | null | undefined;
    return (
      <LookalikeAudienceTraitCalculationReadOnly
        parentModel={parentModel}
        baseAudienceId={baseAudienceId}
        audienceDefinition={
          predictor &&
          ("audienceDefinition" in predictor
            ? predictor?.audienceDefinition
            : predictor?.audience_definition)
        }
        inferenceCadence={predictor?.config?.inferenceCadence}
      />
    );
  }

  if (catalogCollection) {
    return (
      <CatalogRecommendationTraitCalculationReadOnly
        parentModel={parentModel}
        catalogCollection={catalogCollection}
        inferenceCadence={predictor?.config?.inferenceCadence}
        featureDefinitionId={
          predictor &&
          ("featureDefinitionId" in predictor
            ? predictor?.featureDefinitionId
            : "predictor_feature_definition" in predictor
              ? predictor.predictor_feature_definition?.id
              : undefined)
        }
        audienceDefinition={
          predictor &&
          ("audienceDefinition" in predictor
            ? predictor?.audienceDefinition
            : predictor?.audience_definition)
        }
        suppressionFilter={predictor?.suppression_filter}
      />
    );
  }

  return (
    <PredictiveTraitCalculationReadonly
      predictor={predictor}
      parentModel={parentModel}
    />
  );
};

export const CalculationSummary: FC<Readonly<Props>> = ({
  type,
  config: rawConfig,
  parentModel,
  relatedModel,
  hideConditions,
  hideModel,
  predictor,
}) => {
  const {
    aggregatedColumns,
    orderByColumn,
    aggregation,
    conditions = [],
  } = parseTraitConfig(type, rawConfig);

  const isEventModel = Boolean(relatedModel?.event);
  const relatedModelSection = getRelatedModelSection(relatedModel ?? {});

  // An audience membership trait's real configuration lives in
  // tracked_audience_lists, not in trait_definitions.config — the create wizard
  // holds it in a draft under `config.trackedAudienceMembership` until submit.
  // A saved trait's config won't carry it, so the audience clause is only
  // rendered when the draft is present.
  const trackedAudienceMembershipDraft = (
    rawConfig as {
      trackedAudienceMembership?: TrackedAudienceMembershipConfig;
    }
  )?.trackedAudienceMembership;

  const ModelWithIcon = () => (
    <Row gap={1} alignItems="center" ml={0.5}>
      <IconBox
        size="xs"
        color={relatedModelSection.shade}
        icon={relatedModelSection.icon}
      />
      <BoldText>{relatedModel ? relatedModel.name : "--"}</BoldText>
    </Row>
  );

  const summary: {
    value: string | undefined | null;
    Component: FC<{ children: ReactNode }>;
  }[] = [];

  if (type === TraitType.Formula) {
    return (
      <Suspense fallback={null}>
        <FormulaTraitEditor
          columns={parentModel?.filterable_audience_columns ?? []}
          traits={parentModel?.traits ?? []}
          value={aggregation ?? ""}
          readOnly
        />
      </Suspense>
    );
  }

  if (type === TraitType.Predictive) {
    return (
      <PredictiveCalculationSummary
        predictor={predictor}
        parentModel={parentModel}
      />
    );
  }

  if (type === TraitType.TrackedAudienceMembership) {
    summary.push({ value: TRAIT_TYPE_LABELS[type], Component: BoldText });
    if (parentModel) {
      summary.push(
        { value: "on", Component: GrayText },
        { value: parentModel.name, Component: BoldText },
      );
    }
    if (trackedAudienceMembershipDraft) {
      summary.push(
        { value: "for", Component: GrayText },
        {
          value:
            trackedAudienceMembershipDraft.mode === "all"
              ? "all audiences"
              : "selected audiences",
          Component: BoldText,
        },
      );
    }
  } else if (type === TraitType.RawSql) {
    summary.push({ value: aggregation, Component: Code });
  } else {
    summary.push(
      {
        value: TRAIT_TYPE_LABELS[type],
        Component: BoldText,
      },
      {
        value: "of",
        Component: GrayText,
      },
      {
        value: aggregatedColumns?.length
          ? aggregatedColumns
              .map((c) => getPropertyNameFromColumn(c))
              .join(", ")
          : null,
        Component: aggregatedColumns?.length ? BoldText : Fragment,
      },
      {
        value: orderByColumn ? "ordered by" : null,
        Component: orderByColumn ? GrayText : Fragment,
      },
      {
        value: orderByColumn ? getPropertyNameFromColumn(orderByColumn) : null,
        Component: orderByColumn ? BoldText : Fragment,
      },
    );
  }

  // Membership traits have no related model. The create wizard only clears a
  // previously picked `relationship_id` when switching to Formula, so switching
  // to audience membership can leave a stale one behind — ignore it here rather
  // than appending an "of rows in <model>" clause that doesn't apply.
  if (
    relatedModel &&
    !hideModel &&
    type !== TraitType.TrackedAudienceMembership
  ) {
    summary.push(
      {
        value: `${type === TraitType.Count && !aggregatedColumns.length ? "" : "of "}${isEventModel ? "events" : "rows"} in`,
        Component: GrayText,
      },
      {
        value: null,
        Component: ModelWithIcon,
      },
    );
  }

  return (
    <Column bg="base.background" borderRadius="md" gap={4} px={4} py={3}>
      <Row gap={1} flexWrap="wrap">
        {summary.map(({ value, Component }, index) => (
          <Component key={`${value}-${index}`}>{value}</Component>
        ))}
      </Row>
      {!hideConditions && conditions.length > 0 && (
        <ReadOnlyConditions
          audiences={EMPTY_ARRAY}
          relationships={EMPTY_ARRAY}
          traits={EMPTY_ARRAY}
          journeys={EMPTY_ARRAY}
          rootConditions={conditions}
          hideIcons
        />
      )}
    </Column>
  );
};

const BoldText = ({ children }: { children: ReactNode }) => (
  <Text color="text.primary" fontWeight="medium">
    {children}
  </Text>
);

const GrayText = ({ children }: { children: ReactNode }) => (
  <Text color="text.secondary">{children}</Text>
);
